import React, { useCallback, useMemo, useRef, useState } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import XGSLeafletMap from "ui-components/map/leaflet/XGSLeafletMap";
import type { LeafletMarkerModel, LeafletPolylineModel } from "ui-components/map/leaflet/xgsLeafletMap.types";
import { decodeGooglePolylineToLatLng } from "ui-components/map/leaflet/decodeGooglePolyline";
import { TRUCK_MARKER, getStopFillColor } from "services/map";
import { PickupColors, PickupStatus, StopStatus, StopType } from "app/data/common/route";
import { DispatchManifestStopDetailStatus } from "app/data/city-delivery/dispatch-board/models";
import type { XGSSelectOption } from "ui-components/xgs-select/xgsSelect";
import Button, { ButtonSizes, ButtonThemes } from "ui-components/button/button";
import { PickupAssignRequestModel } from "app/data/pickup/models";
import {
  classifyPickupTiming,
  DriverGroup,
  ManifestStopBlock,
  PickupGroupBlock,
} from "./hooks/usePickupAssignmentDriversColumn";
import { useManifestMapForAssignment } from "./hooks/useManifestMapForAssignment";
import type { UnassignedRoute, UnassignedStopGroup } from "./unassignedPickupTypes";
import type { UnassignedPickup } from "./unassignedPickupTypes";
import { useAssignDrivers } from "./hooks/useAssignDrivers";
import {
  PICKUP_ASSIGNMENT_TERMINAL_MARKER_ID,
  usePickupAssignmentActiveTerminalLocation,
} from "./hooks/usePickupAssignmentActiveTerminalLocation";
import "./pickupAssignmentMapPanel.scss";

const PICKUP_UNASSIGNED_YELLOW = "#F2C94C";
const BORDER_LATE = "#EB5757";
const BORDER_AT_RISK = "#F2994A";

const DRIVER_DUTY_STATUS_COLORS: Record<string, string> = {
  AVAILABLE: "#14870A",
  ON_DUTY: "#FFA500",
  OFF_DUTY: "#C43232",
};

function formatDriverDutyStatusLabel(status: string): string {
  return status
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function driverDutyStatusColor(status: string): string {
  return DRIVER_DUTY_STATUS_COLORS[status] ?? "#333";
}

/**
 * Native select in map popups: react-select (v3) + portaled menu + Leaflet/ReactDOM.render fights pointer
 * focus and global handlers; the OS/native picker avoids that entirely.
 */
const PaMapDriverNativeSelect: React.FC<{
  options: XGSSelectOption[];
  value: XGSSelectOption | null;
  onChange: (opt: XGSSelectOption | null) => void;
  placeholder: string;
  disabled?: boolean;
}> = ({ options, value, onChange, placeholder, disabled }) => {
  const v = value?.value != null && value.value !== "" ? String(value.value) : "";
  const blockMap = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <select
      className="pa-map-popup__native-select"
      value={v}
      disabled={disabled}
      aria-label={placeholder}
      onMouseDown={blockMap}
      onPointerDown={blockMap}
      onClick={blockMap}
      onChange={(e) => {
        const s = e.target.value;
        if (!s) {
          onChange(null);
          return;
        }
        const found = options.find((o) => String(o.value) === s);
        onChange(found ?? null);
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {[o.label, o.subtitle].filter(Boolean).join(" — ")}
        </option>
      ))}
    </select>
  );
};

type DispatchColumns = {
  drivers: DriverGroup[];
  unassigned: UnassignedRoute[];
};

/** Pickup marker visibility on the assignment map (legend filters). */
export type PickupMapLegendFilter =
  | "all"
  | "unassigned"
  | "assigned"
  | "completed"
  | "unassigned_late"
  | "unassigned_at_risk"
  | "assigned_late"
  | "assigned_at_risk";

const PICKUP_MAP_FILTER_LABELS: Record<PickupMapLegendFilter, string> = {
  all: "All pickups",
  unassigned: "Unassigned",
  assigned: "Assigned",
  completed: "Completed",
  unassigned_late: "Unassigned · Late",
  unassigned_at_risk: "Unassigned · At Risk",
  assigned_late: "Assigned · Late",
  assigned_at_risk: "Assigned · At Risk",
};

type PickupUrgencyLevel = "late" | "at_risk" | "none";

function urgencyFromUnassignedPickups(pickups: UnassignedPickup[]): PickupUrgencyLevel {
  let atRisk = false;
  for (const p of pickups) {
    const t = classifyPickupTiming(
      p.pickupDateForTiming,
      p.readyTimeForTiming,
      p.closeTimeForTiming,
      p.status[0] ?? ""
    );
    if (t === "late") return "late";
    if (t === "at_risk") atRisk = true;
  }
  return atRisk ? "at_risk" : "none";
}

function urgencyFromAssignedPickups(rows: PickupGroupBlock["pickups"]): PickupUrgencyLevel {
  let atRisk = false;
  for (const p of rows) {
    const t = classifyPickupTiming(p.pickupDate, p.readyTime, p.closeTime, p.status);
    if (t === "late") return "late";
    if (t === "at_risk") atRisk = true;
  }
  return atRisk ? "at_risk" : "none";
}

type PickupMapStats = {
  totalPickups: number;
  unassignedPickups: number;
  assignedPickups: number;
  completedAssignedPickups: number;
  unassignedLatePickups: number;
  unassignedAtRiskPickups: number;
  assignedLatePickups: number;
  assignedAtRiskPickups: number;
};

function computePickupMapStats(columns: DispatchColumns): PickupMapStats {
  let unassignedPickups = 0;
  let unassignedLatePickups = 0;
  let unassignedAtRiskPickups = 0;
  for (const route of columns.unassigned) {
    for (const stop of route.stops) {
      for (const p of stop.pickups) {
        unassignedPickups += 1;
        const t = classifyPickupTiming(
          p.pickupDateForTiming,
          p.readyTimeForTiming,
          p.closeTimeForTiming,
          p.status[0] ?? ""
        );
        if (t === "late") unassignedLatePickups += 1;
        else if (t === "at_risk") unassignedAtRiskPickups += 1;
      }
    }
  }
  let assignedPickups = 0;
  let completedAssignedPickups = 0;
  let assignedLatePickups = 0;
  let assignedAtRiskPickups = 0;
  for (const driver of columns.drivers) {
    for (const s of driver.stops) {
      if (s.kind === "pickup_group") {
        const g = s as PickupGroupBlock;
        assignedPickups += g.totalPickups;
        completedAssignedPickups += g.completedPickups;
        for (const p of g.pickups) {
          const t = classifyPickupTiming(p.pickupDate, p.readyTime, p.closeTime, p.status);
          if (t === "late") assignedLatePickups += 1;
          else if (t === "at_risk") assignedAtRiskPickups += 1;
        }
      }
    }
  }
  return {
    totalPickups: unassignedPickups + assignedPickups,
    unassignedPickups,
    assignedPickups,
    completedAssignedPickups,
    unassignedLatePickups,
    unassignedAtRiskPickups,
    assignedLatePickups,
    assignedAtRiskPickups,
  };
}

function manifestToStopStatus(s: DispatchManifestStopDetailStatus): StopStatus {
  switch (s) {
    case DispatchManifestStopDetailStatus.COMPLETED:
      return StopStatus.COMPLETED;
    case DispatchManifestStopDetailStatus.IN_PROGRESS:
      return StopStatus.ACTIVE;
    default:
      return StopStatus.UPCOMING;
  }
}

function borderClassifyUnassigned(pickups: UnassignedPickup[]): string {
  const u = urgencyFromUnassignedPickups(pickups);
  if (u === "late") return BORDER_LATE;
  if (u === "at_risk") return BORDER_AT_RISK;
  return "transparent";
}

function borderClassifyAssigned(rows: PickupGroupBlock["pickups"]): string {
  const u = urgencyFromAssignedPickups(rows);
  if (u === "late") return BORDER_LATE;
  if (u === "at_risk") return BORDER_AT_RISK;
  return "transparent";
}

function pickupGroupFill(g: PickupGroupBlock): string {
  if (g.completedPickups >= g.totalPickups && g.totalPickups > 0) {
    return getStopFillColor(StopType.PICKUP, PickupStatus.COMPLETED);
  }
  return getStopFillColor(StopType.PICKUP, PickupStatus.ASSIGNED);
}

function shouldShowUnassignedPickupMarker(filter: PickupMapLegendFilter, stop: UnassignedStopGroup): boolean {
  const u = urgencyFromUnassignedPickups(stop.pickups);
  switch (filter) {
    case "all":
    case "unassigned":
      return true;
    case "unassigned_late":
      return u === "late";
    case "unassigned_at_risk":
      return u === "at_risk";
    default:
      return false;
  }
}

function shouldShowAssignedPickupGroupMarker(filter: PickupMapLegendFilter, g: PickupGroupBlock): boolean {
  const groupDone = g.totalPickups > 0 && g.completedPickups >= g.totalPickups;
  const u = urgencyFromAssignedPickups(g.pickups);
  switch (filter) {
    case "all":
    case "assigned":
      return true;
    case "completed":
      return groupDone;
    case "assigned_late":
      return u === "late";
    case "assigned_at_risk":
      return u === "at_risk";
    default:
      return false;
  }
}

type PopupMeta =
  | {
      type: "pickup_unassigned";
      stop: UnassignedStopGroup;
    }
  | {
      type: "pickup_assigned";
      group: PickupGroupBlock;
      driverName: string;
    }
  | {
      type: "driver";
      name: string;
      driverStatus: string;
      trailerLabel: string;
      manifestLabel: string;
      deliveriesCompleted: number;
      numberOfDeliveries: number;
      pickupsCompleted: number;
      numberOfPickups: number;
      lastLocationLine: string;
      formattedAddress?: string;
      locationTimestamp?: string;
    }
  | {
      type: "delivery";
      stop: ManifestStopBlock;
    }
  | {
      type: "terminal";
      name: string;
      airportCode?: string;
      addressLine?: string;
    };

type Props = {
  dispatchColumns: DispatchColumns;
  driverOptions: XGSSelectOption[];
  selectedStopId: string | null;
  selectedDriverDroppableId: string | null;
  onSelectStopOnMap: (stopId: string, driverDroppableId?: string) => void;
  onSelectDriverOnMap: (driverDroppableId: string) => void;
  mapSync: {
    requestFocusStop: (id: string) => void;
    requestFocusDriver: (droppableId: string) => void;
  };
};

const PickupAssignmentMapPanel: React.FC<Props> = ({
  dispatchColumns,
  driverOptions,
  selectedStopId,
  selectedDriverDroppableId,
  onSelectStopOnMap,
  onSelectDriverOnMap,
  mapSync,
}) => {
  const { assignPickupsAsync, isAssigning } = useAssignDrivers();
  const [popupAssignDriver, setPopupAssignDriver] = useState<XGSSelectOption | null>(null);
  const [popupReassignDriver, setPopupReassignDriver] = useState<XGSSelectOption | null>(null);
  const [pickupMapFilter, setPickupMapFilter] = useState<PickupMapLegendFilter>("all");
  const [legendExpanded, setLegendExpanded] = useState(true);

  const pickupMapStats = useMemo(() => computePickupMapStats(dispatchColumns), [dispatchColumns]);
  const { location: activeTerminalLocation } = usePickupAssignmentActiveTerminalLocation();

  const routeDriver = useMemo(() => {
    if (!selectedDriverDroppableId) return null;
    return dispatchColumns.drivers.find((d) => d.id === selectedDriverDroppableId) ?? null;
  }, [dispatchColumns.drivers, selectedDriverDroppableId]);

  const manifestForRoute = routeDriver?.primaryManifestNumber ?? null;
  const { data: manifestMap, isFetching: manifestMapLoading } = useManifestMapForAssignment(manifestForRoute);

  const { markers, polylines, positionsById, popupByMarkerId } = useMemo(() => {
    const list: LeafletMarkerModel[] = [];
    const poly: LeafletPolylineModel[] = [];
    const pos = new Map<string, { lat: number; lng: number }>();
    const popups = new Map<string, PopupMeta>();

    for (const route of dispatchColumns.unassigned) {
      for (const stop of route.stops) {
        if (stop.lat == null || stop.lng == null) continue;
        if (!Number.isFinite(stop.lat) || !Number.isFinite(stop.lng)) continue;
        if (!shouldShowUnassignedPickupMarker(pickupMapFilter, stop)) continue;
        const id = stop.id;
        pos.set(id, { lat: stop.lat, lng: stop.lng });
        popups.set(id, { type: "pickup_unassigned", stop });
        list.push({
          id,
          lat: stop.lat,
          lng: stop.lng,
          kind: "pickup",
          fillColor: PICKUP_UNASSIGNED_YELLOW,
          borderColor: borderClassifyUnassigned(stop.pickups),
          selected: selectedStopId === id,
        });
      }
    }

    for (const driver of dispatchColumns.drivers) {
      for (const s of driver.stops) {
        if (s.kind === "pickup_group") {
          const g = s as PickupGroupBlock;
          if (g.shipperLat == null || g.shipperLng == null) continue;
          if (!Number.isFinite(g.shipperLat) || !Number.isFinite(g.shipperLng)) continue;
          if (!shouldShowAssignedPickupGroupMarker(pickupMapFilter, g)) continue;
          const id = g.id;
          pos.set(id, { lat: g.shipperLat, lng: g.shipperLng });
          popups.set(id, { type: "pickup_assigned", group: g, driverName: driver.name });
          list.push({
            id,
            lat: g.shipperLat,
            lng: g.shipperLng,
            kind: "pickup",
            fillColor: pickupGroupFill(g),
            borderColor: borderClassifyAssigned(g.pickups),
            selected: selectedStopId === id,
          });
        }
      }
    }

    if (activeTerminalLocation) {
      const tid = PICKUP_ASSIGNMENT_TERMINAL_MARKER_ID;
      const { lat, lng } = activeTerminalLocation;
      pos.set(tid, { lat, lng });
      popups.set(tid, {
        type: "terminal",
        name: activeTerminalLocation.name,
        airportCode: activeTerminalLocation.airportCode,
        addressLine: activeTerminalLocation.addressLine,
      });
      list.push({
        id: tid,
        lat,
        lng,
        kind: "service",
        fillColor: getStopFillColor(StopType.SERVICE_CENTER),
        label: "SC",
        selected: false,
      });
    }

    for (const driver of dispatchColumns.drivers) {
      const loc = driver.driverLocation;
      if (!loc) continue;
      const id = `driver-truck-${driver.id}`;
      pos.set(id, { lat: loc.lat, lng: loc.lng });
      popups.set(id, {
        type: "driver",
        name: driver.name,
        driverStatus: driver.driverStatus,
        trailerLabel: driver.trailerLabel,
        manifestLabel: driver.manifestLabel,
        deliveriesCompleted: driver.deliveriesCompleted,
        numberOfDeliveries: driver.numberOfDeliveries,
        pickupsCompleted: driver.pickupsCompleted,
        numberOfPickups: driver.numberOfPickups,
        lastLocationLine: driver.lastLocationLine,
        formattedAddress: loc.formattedAddress,
        locationTimestamp: loc.timestamp,
      });
      list.push({
        id,
        lat: loc.lat,
        lng: loc.lng,
        kind: "driver",
        shapeSvgPath: TRUCK_MARKER.PATH,
        shapeColor: TRUCK_MARKER.COLOR,
        selected: selectedDriverDroppableId === driver.id,
      });
    }

    if (routeDriver && manifestMap?.polyline) {
      const decoded = decodeGooglePolylineToLatLng(manifestMap.polyline);
      if (decoded.length > 1) {
        poly.push({
          id: `manifest-route-${manifestForRoute}`,
          positions: decoded,
          color: "#23356d",
          weight: 5,
          opacity: 0.88,
        });
      }
    }

    if (routeDriver && manifestMap?.markers?.length) {
      const manifestStops = routeDriver.stops.filter(
        (st): st is ManifestStopBlock =>
          st.kind === "manifest_stop" && st.manifestNumber === manifestForRoute
      );
      manifestMap.markers.forEach((mk, i) => {
        if (!Number.isFinite(mk.lat) || !Number.isFinite(mk.lng)) return;
        const block = manifestStops[i];
        const id = block?.id ?? `delivery-${manifestForRoute}-${i}`;
        pos.set(id, { lat: mk.lat, lng: mk.lng });
        if (block) {
          popups.set(id, { type: "delivery", stop: block });
        }
        const lab = mk.label != null ? String(mk.label) : block ? String(block.stopNumber) : `${i + 1}`;
        list.push({
          id,
          lat: mk.lat,
          lng: mk.lng,
          kind: "delivery",
          fillColor: getStopFillColor(
            StopType.STOP,
            block ? manifestToStopStatus(block.stopStatus) : StopStatus.UPCOMING
          ),
          label: lab,
          selected: selectedStopId === id,
        });
      });
    }

    return { markers: list, polylines: poly, positionsById: pos, popupByMarkerId: popups };
  }, [
    dispatchColumns,
    manifestMap,
    manifestForRoute,
    routeDriver,
    selectedStopId,
    selectedDriverDroppableId,
    pickupMapFilter,
    activeTerminalLocation,
  ]);

  /** Refs keep `renderPopup` stable so Leaflet does not ReactDOM.render on every map refresh (remounting react-select kills the menu). */
  const popupByMarkerIdRef = useRef(new Map<string, PopupMeta>());
  const driverOptionsRef = useRef(driverOptions);
  const assignPickupsAsyncRef = useRef(assignPickupsAsync);
  const routeDriverRef = useRef(routeDriver);
  popupByMarkerIdRef.current = popupByMarkerId;
  driverOptionsRef.current = driverOptions;
  assignPickupsAsyncRef.current = assignPickupsAsync;
  routeDriverRef.current = routeDriver;

  const flyTo = useMemo(() => {
    if (selectedStopId && positionsById.has(selectedStopId)) {
      const p = positionsById.get(selectedStopId)!;
      return { lat: p.lat, lng: p.lng, zoom: 15 as number | undefined };
    }
    if (selectedDriverDroppableId) {
      const id = `driver-truck-${selectedDriverDroppableId}`;
      const p = positionsById.get(id);
      if (p) return { lat: p.lat, lng: p.lng, zoom: 14 as number | undefined };
    }
    return null;
  }, [selectedStopId, selectedDriverDroppableId, positionsById]);

  const handleMarkerClick = useCallback(
    (id: string, kind: LeafletMarkerModel["kind"]) => {
      if (kind === "service" && id === PICKUP_ASSIGNMENT_TERMINAL_MARKER_ID) {
        return;
      }
      if (kind === "driver" && id.startsWith("driver-truck-")) {
        const droppableId = id.replace("driver-truck-", "");
        onSelectDriverOnMap(droppableId);
        mapSync.requestFocusDriver(droppableId);
        return;
      }
      if (kind === "pickup") {
        const meta = popupByMarkerIdRef.current.get(id);
        const driverListId = meta && meta.type === "pickup_assigned" ? meta.group.driverListId : undefined;
        onSelectStopOnMap(id, driverListId);
        mapSync.requestFocusStop(id);
        return;
      }
      if (kind === "delivery") {
        onSelectStopOnMap(id, routeDriverRef.current?.id);
        mapSync.requestFocusStop(id);
      }
    },
    [mapSync, onSelectDriverOnMap, onSelectStopOnMap]
  );

  const runAssign = useCallback(
    async (pickupIds: string[], driverId: number | null, status: PickupStatus) => {
      if (!pickupIds.length) return;
      const payload: PickupAssignRequestModel[] = pickupIds.map((pickupId) => ({
        pickupId,
        driverId,
        status,
      }));
      await assignPickupsAsync(payload);
    },
    [assignPickupsAsync]
  );

  const runAssignRef = useRef(runAssign);
  runAssignRef.current = runAssign;

  const renderPopup = useCallback(
    (id: string, kind: LeafletMarkerModel["kind"]) => {
      const meta = popupByMarkerIdRef.current.get(id);
      if (!meta) return null;

      if (meta.type === "terminal") {
        return (
          <div className="pa-map-popup">
            <div className="pa-map-popup__title">{meta.name}</div>
            <div className="pa-map-popup__row">
              <span className="pa-map-popup__label">Terminal</span>
              <span>Service center (SC)</span>
            </div>
            {meta.airportCode ? (
              <div className="pa-map-popup__row">
                <span className="pa-map-popup__label">Code</span>
                <span>{meta.airportCode}</span>
              </div>
            ) : null}
            {meta.addressLine ? (
              <div className="pa-map-popup__row">
                <span className="pa-map-popup__label">Address</span>
                <span>{meta.addressLine}</span>
              </div>
            ) : null}
          </div>
        );
      }

      if (meta.type === "driver") {
        const hasTractor = meta.trailerLabel !== "—";
        const hasManifest = meta.manifestLabel !== "—";
        const equipBits = [
          hasTractor ? meta.trailerLabel : null,
          hasManifest ? meta.manifestLabel : null,
        ].filter((x): x is string => Boolean(x));
        const locationPrimary =
          meta.lastLocationLine !== "—"
            ? meta.lastLocationLine
            : meta.formattedAddress && meta.formattedAddress.trim() !== ""
              ? meta.formattedAddress
              : "—";
        const ts = meta.locationTimestamp;
        const tsValid = Boolean(ts && moment(ts).isValid());
        return (
          <div className="pa-map-popup pa-map-popup--driver">
            <div className="pa-map-popup__title">{meta.name}</div>
            <div className="pa-map-popup__driver-line">
              {equipBits.map((bit, i) => (
                <React.Fragment key={`equip-${i}-${bit}`}>
                  {i > 0 ? (
                    <span className="pa-map-popup__driver-sep" aria-hidden>
                      ·
                    </span>
                  ) : null}
                  <span>{bit}</span>
                </React.Fragment>
              ))}
              {equipBits.length > 0 ? (
                <span className="pa-map-popup__driver-sep" aria-hidden>
                  ·
                </span>
              ) : null}
              <span
                className="pa-map-popup__driver-duty"
                style={{ color: driverDutyStatusColor(meta.driverStatus) }}
              >
                {formatDriverDutyStatusLabel(meta.driverStatus)}
              </span>
            </div>
            <div className="pa-map-popup__driver-stats">
              <span className="pa-map-popup__driver-stat-tag pa-map-popup__driver-stat-tag--delivery">
                <FontAwesomeIcon icon={faArrowDown} className="pa-map-popup__driver-stat-icon" />
                {`${meta.deliveriesCompleted}/${meta.numberOfDeliveries}`}
              </span>
              <span className="pa-map-popup__driver-stat-tag pa-map-popup__driver-stat-tag--pickup">
                <FontAwesomeIcon icon={faArrowUp} className="pa-map-popup__driver-stat-icon" />
                {`${meta.pickupsCompleted}/${meta.numberOfPickups}`}
              </span>
            </div>
            <div className="pa-map-popup__row pa-map-popup__row--location">
              <span className="pa-map-popup__label">
                <FontAwesomeIcon icon={faMapMarkerAlt} aria-hidden className="pa-map-popup__location-icon" />
                Last location
              </span>
              <div className="pa-map-popup__location-block">
                <span>{locationPrimary}</span>
                {tsValid && ts ? (
                  <span className="pa-map-popup__muted">
                    {moment(ts).format("MMM D, YYYY h:mm A")}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        );
      }

      if (meta.type === "delivery") {
        const t = meta.stop;
        return (
          <div className="pa-map-popup pa-map-popup--stop">
            <div className="pa-map-popup__title">Delivery stop {t.stopNumber}</div>
            <div className="pa-map-popup__row">{t.address}</div>
            <div className="pa-map-popup__muted">{t.stopProgressLabel}</div>
          </div>
        );
      }

      if (meta.type === "pickup_unassigned") {
        const t = meta.stop;
        const first = t.pickups[0];
        const pickupIds = t.pickups.map((p) => p.id);
        return (
          <div className="pa-map-popup pa-map-popup--stop">
            <div className="pa-pickup-group__line1" style={{ marginBottom: 8 }}>
              <span className="pa-pickup-group__address">{first?.address ?? "—"}</span>
            </div>
            <div className="pa-map-popup__row">
              <span className="pa-map-popup__label">Pickup window</span>
              <span>{first?.timeRange ?? "—"}</span>
            </div>
            <div className="pa-map-popup__row">
              <span className="pa-map-popup__label">Pickups</span>
              <span>
                {t.pickups.length} {t.pickups.length === 1 ? "pickup" : "pickups"}
              </span>
            </div>
            <div className="pa-map-popup__assign">
              <span className="pa-map-popup__label">Assign driver</span>
              <PaMapDriverNativeSelect
                options={driverOptionsRef.current}
                value={popupAssignDriver}
                onChange={setPopupAssignDriver}
                placeholder="Select driver"
                disabled={isAssigning}
              />
              <Button
                type="button"
                size={ButtonSizes.small}
                theme={ButtonThemes.blue}
                disabled={isAssigning || !popupAssignDriver?.value}
                spinner={isAssigning}
                onClick={() => {
                  const v = popupAssignDriver?.value;
                  if (v == null || v === "") return;
                  const n = Number(v);
                  if (Number.isNaN(n)) return;
                  void runAssignRef.current(pickupIds, n, PickupStatus.ASSIGNED);
                }}
              >
                Assign
              </Button>
            </div>
          </div>
        );
      }

      if (meta.type === "pickup_assigned") {
        const g = meta.group;
        const allDone = g.completedPickups >= g.totalPickups && g.totalPickups > 0;
        const activeIds = g.pickups.filter((p) => p.status !== PickupStatus.COMPLETED).map((p) => p.id);

        return (
          <div className="pa-map-popup pa-map-popup--stop">
            <div className="pa-pickup-group__line1" style={{ marginBottom: 8 }}>
              <span className="pa-pickup-group__address">{g.shipperAddress}</span>
            </div>
            <div className="pa-map-popup__row">
              <span className="pa-map-popup__label">Pickup window</span>
              <span>{g.pickups[0]?.timeRange ?? "—"}</span>
            </div>
            <div className="pa-map-popup__row">
              <span className="pa-map-popup__label">Pickups</span>
              <span>
                {g.pickups.length} {g.pickups.length === 1 ? "pickup" : "pickups"}
              </span>
            </div>
            <div className="pa-map-popup__row">
              <span className="pa-map-popup__label">Driver</span>
              <span>{meta.driverName}</span>
            </div>

            {!allDone ? (
              <>
                <div className="pa-map-popup__assign">
                  <span className="pa-map-popup__label">Reassign to</span>
                  <PaMapDriverNativeSelect
                    options={driverOptionsRef.current}
                    value={popupReassignDriver}
                    onChange={setPopupReassignDriver}
                    placeholder="Select driver"
                    disabled={isAssigning}
                  />
                  <Button
                    type="button"
                    size={ButtonSizes.small}
                    theme={ButtonThemes.blue}
                    disabled={isAssigning || !popupReassignDriver?.value}
                    spinner={isAssigning}
                    onClick={() => {
                      const v = popupReassignDriver?.value;
                      if (v == null || v === "") return;
                      const n = Number(v);
                      if (Number.isNaN(n)) return;
                      const unassign: PickupAssignRequestModel[] = activeIds.map((pickupId) => ({
                        pickupId,
                        driverId: null,
                        status: PickupStatus.UNASSIGNED,
                      }));
                      const assign: PickupAssignRequestModel[] = activeIds.map((pickupId) => ({
                        pickupId,
                        driverId: n,
                        status: PickupStatus.ASSIGNED,
                      }));
                      const doAssign = assignPickupsAsyncRef.current;
                      void doAssign(unassign).then(() => doAssign(assign));
                    }}
                  >
                    Reassign
                  </Button>
                </div>
                <Button
                  type="button"
                  size={ButtonSizes.small}
                  theme={ButtonThemes.gray}
                  disabled={isAssigning || activeIds.length === 0}
                  spinner={isAssigning}
                  onClick={() => void runAssignRef.current(activeIds, null, PickupStatus.UNASSIGNED)}
                >
                  Unassign
                </Button>
              </>
            ) : null}
          </div>
        );
      }

      return null;
    },
    [popupAssignDriver, popupReassignDriver, isAssigning]
  );

  const legendFilterBtn = (id: PickupMapLegendFilter, swatch: React.ReactNode, label: string, count: number, sub?: string) => (
    <button
      type="button"
      className={`pa-map-legend__filter${pickupMapFilter === id ? " pa-map-legend__filter--active" : ""}`}
      onClick={() => setPickupMapFilter(id)}
      aria-pressed={pickupMapFilter === id}
    >
      <span className="pa-map-legend__filter-main">
        {swatch}
        <span className="pa-map-legend__filter-label">
          {label} <span className="pa-map-legend__filter-count">({count})</span>
        </span>
      </span>
      {sub ? <span className="pa-map-legend__filter-sub">{sub}</span> : null}
    </button>
  );

  const {
    totalPickups,
    unassignedPickups,
    assignedPickups,
    completedAssignedPickups,
    unassignedLatePickups,
    unassignedAtRiskPickups,
    assignedLatePickups,
    assignedAtRiskPickups,
  } = pickupMapStats;

  const assignedSub =
    assignedPickups > 0 ? `${completedAssignedPickups} of ${assignedPickups} done` : undefined;

  const legend = (
    <div className={`pa-map-legend${legendExpanded ? " pa-map-legend--expanded" : " pa-map-legend--collapsed"}`}>
      <button
        type="button"
        className="pa-map-legend__collapse-toggle"
        onClick={() => setLegendExpanded((v) => !v)}
        aria-expanded={legendExpanded}
        aria-controls="pa-map-legend-body"
      >
        <span className="pa-map-legend__collapse-toggle-text">
          <span className="pa-map-legend__collapse-title">Map legend</span>
          {!legendExpanded ? (
            <span className="pa-map-legend__collapse-filter"> · {PICKUP_MAP_FILTER_LABELS[pickupMapFilter]}</span>
          ) : null}
        </span>
        <span className="pa-map-legend__collapse-chevron" aria-hidden />
      </button>
      {legendExpanded ? (
        <div className="pa-map-legend__body" id="pa-map-legend-body">
          <div className="pa-map-legend__title">Pickups on map</div>
          {legendFilterBtn(
            "all",
            <span
              className="pa-map-legend__swatch pa-map-legend__swatch--pu"
              style={{
                background: `linear-gradient(90deg, ${PICKUP_UNASSIGNED_YELLOW} 50%, ${PickupColors[PickupStatus.ASSIGNED]} 50%)`,
              }}
              aria-hidden
            />,
            "All pickups",
            totalPickups
          )}
          {legendFilterBtn(
            "unassigned",
            <span className="pa-map-legend__swatch pa-map-legend__swatch--pu" style={{ background: PICKUP_UNASSIGNED_YELLOW }} aria-hidden />,
            "Unassigned",
            unassignedPickups
          )}
          {legendFilterBtn(
            "assigned",
            <span
              className="pa-map-legend__swatch pa-map-legend__swatch--pu"
              style={{ background: getStopFillColor(StopType.PICKUP, PickupStatus.ASSIGNED) }}
              aria-hidden
            />,
            "Assigned",
            assignedPickups,
            assignedSub
          )}
          {legendFilterBtn(
            "completed",
            <span
              className="pa-map-legend__swatch pa-map-legend__swatch--pu"
              style={{ background: getStopFillColor(StopType.PICKUP, PickupStatus.COMPLETED) }}
              aria-hidden
            />,
            "Completed",
            completedAssignedPickups
          )}
          <div className="pa-map-legend__title">Late and At Risk (pickups)</div>
          {legendFilterBtn(
            "unassigned_late",
            <span className="pa-map-legend__swatch pa-map-legend__swatch--ring" style={{ borderColor: BORDER_LATE }} aria-hidden />,
            PICKUP_MAP_FILTER_LABELS.unassigned_late,
            unassignedLatePickups
          )}
          {legendFilterBtn(
            "unassigned_at_risk",
            <span className="pa-map-legend__swatch pa-map-legend__swatch--ring" style={{ borderColor: BORDER_AT_RISK }} aria-hidden />,
            PICKUP_MAP_FILTER_LABELS.unassigned_at_risk,
            unassignedAtRiskPickups
          )}
          {legendFilterBtn(
            "assigned_late",
            <span className="pa-map-legend__swatch pa-map-legend__swatch--ring" style={{ borderColor: BORDER_LATE }} aria-hidden />,
            PICKUP_MAP_FILTER_LABELS.assigned_late,
            assignedLatePickups
          )}
          {legendFilterBtn(
            "assigned_at_risk",
            <span className="pa-map-legend__swatch pa-map-legend__swatch--ring" style={{ borderColor: BORDER_AT_RISK }} aria-hidden />,
            PICKUP_MAP_FILTER_LABELS.assigned_at_risk,
            assignedAtRiskPickups
          )}
          <div className="pa-map-legend__title">Terminal</div>
          <div className="pa-map-legend__row">
            <span
              className="pa-map-legend__swatch pa-map-legend__swatch--pu"
              style={{ background: getStopFillColor(StopType.SERVICE_CENTER) }}
              aria-hidden
            />
            This terminal (SC)
            {activeTerminalLocation ? (
              <span className="pa-map-legend__terminal-name"> — {activeTerminalLocation.name}</span>
            ) : null}
          </div>
          <div className="pa-map-legend__title">Driver</div>
          <div className="pa-map-legend__row">
            <span className="pa-map-legend__truck" aria-hidden>
              <svg width="22" height="22" viewBox="0 0 32 32" fill={TRUCK_MARKER.COLOR}>
                <path d={TRUCK_MARKER.PATH} />
              </svg>
            </span>
            Last known location
          </div>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="pa-assignment-map-panel">
      {manifestMapLoading && manifestForRoute ? (
        <div className="pa-assignment-map-panel__banner">Loading route…</div>
      ) : null}
      <XGSLeafletMap
        className="pa-assignment-map-panel__map"
        markers={markers}
        polylines={polylines}
        flyTo={flyTo}
        onMarkerClick={handleMarkerClick}
        renderPopup={renderPopup}
        overlay={legend}
        useClustering
        clusteringThreshold={45}
      />
    </div>
  );
};

export default PickupAssignmentMapPanel;
